import { useEffect } from 'react';
import { useNotifications, useMarkAllRead, useUnreadCount } from '@/hooks/useNotifications';
import { NotificationItem } from '@/components/notifications/NotificationItem';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { Button } from '@/components/ui/Button';
import { haptic } from '@/lib/telegram';
import styles from './NotificationsPage.module.css';

export const NotificationsPage = () => {
  const { data, isLoading } = useNotifications();
  const { data: unreadCount } = useUnreadCount();
  const markAllRead = useMarkAllRead();

  const handleMarkAll = () => {
    haptic.medium();
    markAllRead.mutate();
  };

  // Авто-пометка непрочитанных после 3 секунд просмотра.
  // Это поведение, как в Instagram/Telegram — открыл вкладку, через секунду
  // бейдж исчез, потому что ты эти уведомления уже увидел.
  useEffect(() => {
    if (!data || !unreadCount || unreadCount === 0) return;
    const timer = setTimeout(() => {
      markAllRead.mutate();
    }, 3000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.length]);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <p className={styles.eyebrow}>Активность</p>
          <h1 className={styles.title}>Уведомления</h1>
        </div>
        {unreadCount !== undefined && unreadCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleMarkAll}
            loading={markAllRead.isPending}
          >
            Прочитать всё
          </Button>
        )}
      </header>

      <div className={styles.content}>
        {isLoading ? (
          <div className={styles.list}>
            <Skeleton height={92} />
            <Skeleton height={92} />
            <Skeleton height={92} />
          </div>
        ) : !data || data.length === 0 ? (
          <EmptyState
            icon={
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.7 21a2 2 0 0 1-3.4 0" />
              </svg>
            }
            title="Пока тихо"
            description="Здесь появятся события по твоим аукционам и подпискам"
          />
        ) : (
          <>
            {unreadCount !== undefined && unreadCount > 0 && (
              <p className={styles.counter}>
                <span className={styles.counterDot} />
                {unreadCount} {pluralUnread(unreadCount)}
              </p>
            )}

            <div className={styles.list}>
              {data.map((n) => (
                <NotificationItem key={n.id} notification={n} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const pluralUnread = (n: number): string => {
  const last = n % 10;
  const lastTwo = n % 100;
  if (lastTwo >= 11 && lastTwo <= 14) return 'новых';
  if (last === 1) return 'новое';
  if (last >= 2 && last <= 4) return 'новых';
  return 'новых';
};